import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtCurrency, niceName } from "@/lib/format";
import { ArrowRight, TrendingDown, TrendingUp } from "lucide-react";
import { ReviewQueue } from "@/components/ReviewQueue";
import { ProcessTable } from "@/components/ProcessTable";
import { CommissionChart, MONTHS, kEur } from "@/components/CommissionChart";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const card = "bg-white rounded-[16px] p-4 px-5 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0";
const T = ({ children, sub }) => (
  <div><h2 className="font-body font-semibold text-[17px] text-ec-green">{children}</h2>{sub && <p className="text-[13px] text-slate-500">{sub}</p>}</div>
);
const PERIODS = [["mes", "Mês"], ["trimestre", "Trimestre"], ["ano", "Ano"]];
const PERIOD_IN = { mes: "no mês", trimestre: "no trimestre", ano: "no ano" };
const PHASES = [["angariacao", "Angariação"], ["pronto", "CMI assinado"], ["proposta", "Proposta aceite"], ["cpcv", "CPCV"], ["escritura", "Escritura marcada"]];
const LOST_COLORS = { proprietario: "#7B5EA7", comprador: "#22324a", credito: "#C98A3A", avaliacao: "#5F8F6B", documental: "#8A8F98", outro: "#C4C8CF", outra_agencia: "#9DB4CF", cmi_terminou: "#B7AFA3" };
const LOST_SHORT = { proprietario: "Desist. proprietário", comprador: "Desist. comprador", credito: "Crédito recusado", avaliacao: "Avaliação bancária", documental: "Problema documental", outro: "Outro", outra_agencia: "Outra agência", cmi_terminou: "CMI terminou" };
const STATE = { red: "bg-red-50 text-red-700", amber: "bg-amber-50 text-amber-800", green: "bg-emerald-50 text-emerald-700" };
const num = "font-body tabular-nums";

function Var({ v, year }) {
  if (v == null) return <span className="text-slate-400">sem comparação com {year}</span>;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="card-var">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset ${up ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`} data-testid={up ? "card-var-up" : "card-var-down"}><Icon size={13} />{up ? "+" : "−"}{Math.abs(v)}%</span>
      <span className="text-slate-500">vs. {year}</span>
    </span>
  );
}

function Cards({ c }) {
  const items = [
    ["listings", "Angariações", c.listings, <><Var v={c.listings_var} year={c.prev_year} /> · {c.cmi_active} CMI ativos</>],
    ["deeds", "Escrituras", c.deeds, <>{c.volume ? kEur(c.volume) : "0 €"} de volume · <Var v={c.deeds_var} year={c.prev_year} /></>],
    ["billed", "Comissão faturada", fmtCurrency(c.billed), c.billed_avg ? `Média ${fmtCurrency(c.billed_avg)} por escritura` : "Sem escrituras no período"],
    ["forecast", "Comissão prevista · próximos 60 dias", fmtCurrency(c.forecast), `${c.forecast_deeds} ${c.forecast_deeds === 1 ? "escritura marcada" : "escrituras marcadas"}`],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="overview-cards">
      {items.map(([k, l, v, sub]) => (
        <div key={k} className={card} data-testid={`overview-card-${k}`}>
          <div className="text-sm text-slate-500">{l}</div>
          <div className={`${num} text-[28px] leading-tight font-bold navy-text`} data-testid={`overview-card-${k}-value`}>{v}</div>
          <div className="text-[13px] text-slate-500 leading-6">{sub}</div>
        </div>
      ))}
    </div>
  );
}

function Chart({ chart }) {
  const cur = chart.find((m) => m.offset === 0);
  return (
    <div className={`${card} flex flex-col lg:col-start-1 lg:row-start-1 lg:row-span-2`} data-testid="overview-chart">
      <div className="flex items-start justify-between gap-3 mb-3">
        <T sub={`Faturada até ${MONTHS[Number(cur.month.slice(5, 7)) - 1].toLowerCase()} · prevista pelas escrituras marcadas`}>Comissão por mês</T>
        <div className="flex gap-3 text-xs text-slate-500 shrink-0">
          <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bar-brand-v" />Faturada</span>
          <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bar-brand-soft" />Prevista</span>
        </div>
      </div>
      <CommissionChart data={chart} />
    </div>
  );
}

function Phases({ pipeline, onOpen }) {
  const max = Math.max(1, ...Object.values(pipeline));
  return (
    <div className={`${card} lg:col-start-2 lg:row-start-1 lg:rounded-b-none lg:pb-3`} data-testid="overview-pipeline">
      <T sub="Processos ativos hoje">Processos por fase</T>
      <div className="mt-2">
        {PHASES.map(([k, l]) => (
          <button key={k} onClick={() => onOpen({ title: `Fase: ${l}`, params: { phase: k } })} data-testid={`overview-phase-${k}`}
            className="w-full grid grid-cols-[130px_1fr_28px] items-center gap-3 text-sm text-slate-700 rounded-md px-1 py-[2px] hover:bg-slate-50 transition-colors text-left">
            <span className="whitespace-nowrap">{l}</span>
            <span className="h-3.5 rounded-full bar-track"><span className="block h-3.5 rounded-full bar-brand transition-[width] duration-700" style={{ width: `${(pipeline[k] / max) * 100}%` }} /></span>
            <span className={`${num} font-semibold navy-text text-right`}>{pipeline[k]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Lost({ lost }) {
  return (
    <div className={`${card} lg:col-start-2 lg:row-start-2 lg:rounded-t-none lg:-mt-4 lg:border-t-0 lg:pt-0`} data-testid="overview-lost">
      <div className="hidden lg:block border-t border-slate-100 mb-3" />
      <T sub={<span className={num}>{lost.count} este ano · {fmtCurrency(lost.commission)} de comissão</span>}>Negócios perdidos</T>
      {lost.count === 0 ? <p className="text-sm text-slate-500 mt-3" data-testid="overview-lost-empty">Nenhum negócio perdido este ano.</p> : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden gap-0.5 mt-3" data-testid="overview-lost-bar">
            {lost.reasons.map((r) => <span key={r.code} title={`${r.label}: ${r.count} · ${fmtCurrency(r.commission)}`} style={{ flexGrow: r.count, background: LOST_COLORS[r.code] }} />)}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2.5" data-testid="overview-lost-reasons">
            {lost.reasons.map((r) => (
              <span key={r.code} className="inline-flex items-center gap-1.5 text-xs text-slate-600 min-w-0" data-testid={`overview-lost-reason-${r.code}`}>
                <i className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: LOST_COLORS[r.code] }} /><span className="truncate">{LOST_SHORT[r.code]}</span><span className={`${num} text-slate-400`}>{r.count}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const PendingRow = ({ r }) => (
  <li>
    <Link to={`/processo/${r.id}`} className="grid grid-cols-[48px_minmax(0,1fr)_auto] sm:grid-cols-[48px_minmax(0,1fr)_96px_170px] items-center gap-x-3 gap-y-1 py-2.5 hover:bg-slate-50 rounded-md transition-colors" data-testid={`overview-pending-${r.ref}`}>
      <div className={`w-12 h-12 rounded-lg border grid place-items-center text-center ${r.tone === "red" && r.days <= 7 ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`overview-pending-date-${r.ref}`}>
        <div><div className="text-lg font-bold navy-text leading-none tabular-nums">{r.day}</div><div className="text-[10px] font-semibold text-slate-600 mt-0.5">{r.wd}</div></div>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold navy-text" title={r.title}>{r.title}</div>
        <div className="text-xs text-slate-500">{[r.consultant || "Sem consultor", r.gestor && `gestão: ${r.gestor.split(" ")[0]}`].filter(Boolean).join(" · ")}</div>
      </div>
      <span className={`${num} text-sm font-semibold text-right ${r.commission ? "navy-text" : "text-slate-400"}`} data-testid={`overview-pending-commission-${r.ref}`}>{r.commission ? fmtCurrency(r.commission) : "Por definir"}</span>
      <div className="col-span-3 sm:col-span-1 pl-[60px] sm:pl-0 min-w-0">
        <span className={`inline-block px-2.5 py-0.5 rounded-md text-sm font-semibold ${STATE[r.tone]}`} data-testid={`overview-pending-state-${r.ref}`}>{r.state}</span>
        {r.why && <div className="text-xs text-slate-500 mt-0.5">{r.why}</div>}
      </div>
    </Link>
  </li>
);

function Pending({ items, total }) {
  const [all, setAll] = useState(false);
  const sub = <span className={num}>Próximos 30 dias · {fmtCurrency(total)} de comissão prevista</span>;
  return (
    <div className={`${card} lg:col-start-1 lg:row-start-3`} data-testid="overview-pending">
      <T sub={sub}>Escrituras com pendentes · <span className={num}>{items.length}</span></T>
      {items.length === 0 ? <p className="text-sm text-green-700 mt-3" data-testid="overview-pending-empty">Nenhuma escritura com pendentes.</p> : (
        <ul className="mt-1 divide-y divide-slate-100">{items.slice(0, 3).map((r) => <PendingRow key={r.id} r={r} />)}</ul>
      )}
      {items.length > 3 && (
        <button onClick={() => setAll(true)} className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-ec-green hover:underline" data-testid="overview-pending-all">Ver as {items.length} <ArrowRight size={14} /></button>
      )}
      <Dialog open={all} onOpenChange={setAll}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pending-all-dialog">
          <DialogHeader><DialogTitle className="font-heading text-ec-green">Escrituras com pendentes · {items.length}</DialogTitle><DialogDescription>Escrituras marcadas nos próximos 30 dias que ainda não estão prontas.</DialogDescription></DialogHeader>
          <ul className="divide-y divide-slate-100">{items.map((r) => <PendingRow key={r.id} r={r} />)}</ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Top({ top, period }) {
  const max = Math.max(1, ...top.map((r) => r.volume));
  return (
    <div className={`${card} lg:col-start-2 lg:row-start-3`} data-testid="overview-top">
      <T sub={`Volume de escrituras ${PERIOD_IN[period]}`}>Top consultores</T>
      {top.length === 0 ? <p className="text-sm text-slate-500 mt-3">Sem escrituras no período.</p> : (
        <div className="mt-2 space-y-1.5">
          {top.map((r) => (
            <div key={r.id} className="grid grid-cols-[140px_1fr_64px] items-center gap-3 text-sm" data-testid={`overview-top-${r.id}`}>
              <span className="text-slate-700 leading-tight">{niceName(r.name)}</span>
              <span className="h-3.5 rounded-full bar-track"><span className="block h-3.5 rounded-full bar-brand transition-[width] duration-700" style={{ width: `${(r.volume / max) * 100}%` }} /></span>
              <span className={`${num} font-semibold navy-text text-right`}>{kEur(r.volume)}</span>
            </div>
          ))}
        </div>
      )}
      <Link to="/equipa" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-ec-green hover:underline" data-testid="overview-team-link">Ver a equipa <ArrowRight size={14} /></Link>
    </div>
  );
}

function Compliance({ areas = [] }) {
  return (
    <div className={card} data-testid="overview-compliance">
      <T sub="Certidão e caderneta com mais de 10% de diferença">Compliance</T>
      {areas.length === 0 ? <p className="text-sm text-emerald-700 mt-3" data-testid="overview-areas-none">Sem diferenças de áreas por resolver.</p> : (
        <div className="mt-2 divide-y divide-slate-100">
          {areas.map((r) => (
            <Link key={r.pid} to={`/processo/${r.pid}#sec-imovel`} className="flex items-center justify-between py-2 text-sm hover:text-ec-green" data-testid={`overview-area-${r.pid}`}>
              <span className="text-slate-700">{r.short}</span>
              <span className={`text-xs font-semibold ${r.block ? "text-red-700" : "text-amber-700"}`}>{r.block ? "Bloqueia CPCV e escritura" : "Antes do CPCV"}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ period, setPeriod, at }) {
  const t = at ? new Date(at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
      <div>
        <h1 className="font-heading text-3xl text-ec-green">Visão da agência</h1>
        <p className="text-sm text-slate-500 mt-0.5" data-testid="overview-updated">Atualizado hoje, <span className={num}>{t}</span> · comissões líquidas de partilha, sem IVA</p>
      </div>
      <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-fit" data-testid="overview-period">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`overview-period-${k}`}
            className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${period === k ? "navy-bg text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function Visao() {
  const [period, setPeriod] = useState("ano");
  const [d, setD] = useState(null);
  const [procs, setProcs] = useState([]);
  const [query, setQuery] = useState(null);
  useEffect(() => { api.get("/agency/overview", { params: { period } }).then(({ data }) => setD(data)); }, [period]);
  useEffect(() => { if (d?.show_review) { api.get("/processes").then(({ data }) => setProcs(data)); } }, [d?.show_review]);
  if (!d) return <div className="text-slate-400 py-20 text-center">A carregar…</div>;
  return (
    <div className="space-y-4 -mt-4 md:-mt-6" data-testid="overview-page">
      <Header period={period} setPeriod={setPeriod} at={d.generated_at} />
      <Cards c={d.cards} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
        <Chart chart={d.chart} />
        <Pending items={d.pending} total={d.pending_total} />
        <Phases pipeline={d.pipeline} onOpen={setQuery} />
        <Top top={d.top} period={period} />
        <Lost lost={d.lost} />
        <Compliance areas={d.compliance?.areas} />
      </div>
      {d.show_review && <ReviewQueue procs={procs} />}
      <ProcessTable query={query} onClose={() => setQuery(null)} />
    </div>
  );
}
